import { useQuery } from '@tanstack/react-query';
import { jiraConfigService, JiraConfigResponse } from '../services/jiraConfigService';

/**
 * Hook to check Jira configuration status
 */
export const useJiraConfig = () => {
  return useQuery({
    queryKey: ['jira-config'],
    queryFn: () => jiraConfigService.getCurrentConfig(),
    staleTime: 30000, // 30 seconds
    refetchOnWindowFocus: false,
    retry: 2,
    select: (data) => {
      if (!data.success) {
        return {
          isConfigured: false,
          baseUrl: '',
          email: '',
          hasApiToken: false,
          error: 'Failed to check configuration'
        };
      }
      return data.data;
    }
  });
};

/**
 * Hook that only runs when Jira is properly configured
 */
export const useJiraConfiguredQuery = <T>(
  queryKey: any[],
  queryFn: () => Promise<T>,
  options: any = {}
) => {
  const { data: jiraConfig, isLoading: configLoading } = useJiraConfig();

  // Extract retry-related options to prevent overrides that cause request loops
  const { retry, retryOnMount, retryDelay, staleTime, ...safeOptions } = options;

  return useQuery<T>({
    // Allow most options to be customized
    ...safeOptions,
    // But enforce these critical settings to prevent request loops
    retry: false, // Always disable React Query retry since API service handles retries
    retryOnMount: false, // Always prevent retry on component mount
    refetchOnWindowFocus: false, // Always prevent refetch on window focus
    staleTime: staleTime || 2 * 60 * 1000, // Allow override but default to 2 minutes
    queryKey,
    queryFn,
    enabled: !configLoading && jiraConfig?.isConfigured === true && (options.enabled !== false),
  });
};
